<script setup lang="ts">
import { useUIStore } from '~/store/ui';
import { useAuthStore } from '~/store/auth';

const { t } = useI18n();

const uiStore = useUIStore();
const authStore = useAuthStore();
const route = useRoute();

const isOpen = computed(() => uiStore.authPromptOpen && !authStore.token);

const returnTo = computed(() => route.fullPath);

const registerHref = computed(() => `${'/auth/register'}?returnTo=${encodeURIComponent(returnTo.value)}`);
const loginHref = computed(() => `${'/auth/login'}?returnTo=${encodeURIComponent(returnTo.value)}`);

const close = () => {
  uiStore.setAuthPromptOpen(false);
};

// Close on Escape for accessibility.
const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) return;
  if (event.key === 'Escape') close();
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[100] flex items-center justify-center px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-prompt-title"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-md"
          @click="close"
        />
        <div
          class="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
          @click.stop
        >
          <button
            type="button"
            class="absolute end-3 top-3 text-black-60 hover:text-black-100"
            :aria-label="t('common.actions.close')"
            @click="close"
          >
            <svg
              class="w-5 h-5"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
          <div class="flex flex-col items-center text-center">
            <div class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF4E0]">
              <span class="text-2xl">🔒</span>
            </div>
            <h3
              id="auth-prompt-title"
              class="text-xl font-bold text-black-100"
            >
              {{ t('common.authPromptModal.title') }}
            </h3>
            <p class="mt-2 text-sm text-black-60">
              {{ t('common.authPromptModal.description') }}
            </p>
            <div class="mt-5 flex w-full flex-col gap-2">
              <NuxtLink
                :to="registerHref"
                class="inline-flex h-11 w-full items-center justify-center rounded-xl bg-[#FFB648] text-sm font-semibold text-black-100 hover:opacity-90 transition-opacity"
              >
                {{ t('common.authPromptModal.applyNow') }}
              </NuxtLink>
              <NuxtLink
                :to="loginHref"
                class="inline-flex h-11 w-full items-center justify-center rounded-xl border border-black-20 bg-white text-sm font-medium text-black-100 hover:bg-black-03 transition-colors"
              >
                {{ t('common.authPromptModal.logIn') }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
